import React from 'react';
import { CompanyProjectSubmissionResponse } from '../../api/admin';
import styled, { useTheme } from 'styled-components';
import dayjs from 'dayjs';
import { CollapsibleSection } from './ReplyToSOSQuestion';
import { H4, P, PSmall } from '../elements/Text';
import { MultiLineInput } from '../elements/Input';
import Button from '../elements/Button';
import { getMilestoneStyles } from './CompanyProjects';
import { useMutation } from 'react-query';
import useAuth from '../../hooks/use-auth';
import { postCommentProjectSubmission } from '../../api/admin';
import { toast } from 'react-toastify';

const QAPair = styled.div`
  font-size: 14px;
`;
const Question = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
  color: ${({ theme }) => theme.colors.textRegular};
`;
const Answer = styled.div`
  color: ${({ theme }) => theme.colors.inputTextActive};
  margin-bottom: 8px;
`;
const QABlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionEmpty = styled.div`
  color: ${({ theme }) => theme.colors.textPlaceholder};
  font-style: italic;
  font-size: 14px;
`;

const CohortInfo = styled(P)`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const SectionContent = styled.div`
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const HeaderRowLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const HeaderStatus = styled.div<{ color?: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ color, theme }) => color || theme.colors.textPlaceholder};
  margin-left: 8px;
`;

const MetadataText = styled(PSmall)`
  margin: 0;
`;

const SubmissionMetadata = ({
  submission,
}: {
  submission:
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_CHECKIN_1']
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_CHECKIN_2']
    | CompanyProjectSubmissionResponse['submissions'][number]['PROJECT_UPLOAD'];
}) => {
  if (!submission) return null;
  const unlockDate = submission.unlockedAt;
  const isLocked = submission.isLocked;
  const notifications = submission.notifications || [];
  const submittedAt = submission.submittedAt;

  return (
    <>
      <MetadataText>
        <b>Status:</b> {isLocked ? 'Locked' : 'Unlocked'}
        {unlockDate && (
          <>
            {' '}
            ({isLocked ? 'Unlocks' : 'Unlocked'} at{' '}
            {dayjs(unlockDate).format('MMM D, YYYY')})
          </>
        )}
      </MetadataText>
      {notifications.length > 0 && notifications.some((n) => !!n.sent_at) && (
        <MetadataText>
          <b>Notification sent:</b>{' '}
          {notifications
            .filter((n) => !!n.sent_at)
            .map((n) => dayjs(n.sent_at).format('MMM D, YYYY'))
            .join(', ')}
        </MetadataText>
      )}
      {submittedAt && (
        <MetadataText>
          <b>Submitted at:</b> {dayjs(submittedAt).format('MMM D, YYYY')}
        </MetadataText>
      )}
    </>
  );
};

const ExternalLinks = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 0;
`;
const ExternalLink = styled.a`
  font-size: 14px;
  margin-top: 0;
`;

const Files = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 0;
`;

export default function CompanyProjectSubmission({
  submission,
  submissionId,
  companyId,
  refetchSubmissions,
}: {
  submission: CompanyProjectSubmissionResponse['submissions'][number];
  submissionId: number;
  companyId: number;
  refetchSubmissions: () => void;
}) {
  const theme = useTheme();
  const { cohort, PROJECT_CHECKIN_1, PROJECT_CHECKIN_2, PROJECT_UPLOAD } =
    submission;
  const formatDate = (date: Date | string) => dayjs(date).format('MMM D, YYYY');
  const { getTokenSilently } = useAuth();

  const latestSubmitted = [
    PROJECT_UPLOAD,
    PROJECT_CHECKIN_2,
    PROJECT_CHECKIN_1,
  ].find((submission) => submission?.isCompleted);

  const projectCheckin1 = getMilestoneStyles(PROJECT_CHECKIN_1, cohort, theme);
  const projectCheckin2 = getMilestoneStyles(PROJECT_CHECKIN_2, cohort, theme);
  const projectUpload = getMilestoneStyles(PROJECT_UPLOAD, cohort, theme);

  const [submissionComment, setSubmissionComment] = React.useState(
    submission.comment?.comment ?? ''
  );

  const commentMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      await postCommentProjectSubmission(token, {
        companyId,
        submissionId,
        comment: submissionComment,
      });
    },
    onSuccess: async () => {
      await refetchSubmissions();
      toast.success('Comment saved');
    },
  });

  return (
    <Wrapper>
      <CohortInfo>
        <b>Cohort:</b> {formatDate(cohort.startDate)} -{' '}
        {formatDate(cohort.endDate)}
      </CohortInfo>
      <CollapsibleSection
        title={
          <HeaderRow>
            <HeaderRowLeft>Checkin #1</HeaderRowLeft>
            <HeaderStatus color={projectCheckin1.badgeColor}>
              {projectCheckin1.status}
            </HeaderStatus>
          </HeaderRow>
        }
        isOpenByDefault={latestSubmitted === PROJECT_CHECKIN_1}
      >
        <SectionContent>
          <SubmissionMetadata submission={PROJECT_CHECKIN_1} />
          <QABlock>
            <MetadataText>
              <b>Survey</b>
            </MetadataText>
            {PROJECT_CHECKIN_1 && PROJECT_CHECKIN_1.survey.length > 0 ? (
              PROJECT_CHECKIN_1.survey.map((qa, idx) => (
                <QAPair key={idx}>
                  <Question>Q: {qa.question}</Question>
                  <Answer>A: {qa.answer}</Answer>
                </QAPair>
              ))
            ) : (
              <SectionEmpty>No survey submitted.</SectionEmpty>
            )}
          </QABlock>
        </SectionContent>
      </CollapsibleSection>
      <CollapsibleSection
        title={
          <HeaderRow>
            <HeaderRowLeft>Checkin #2</HeaderRowLeft>
            <HeaderStatus color={projectCheckin2.badgeColor}>
              {projectCheckin2.status}
            </HeaderStatus>
          </HeaderRow>
        }
        isOpenByDefault={latestSubmitted === PROJECT_CHECKIN_2}
      >
        <SectionContent>
          <SubmissionMetadata submission={PROJECT_CHECKIN_2} />
          <QABlock>
            <MetadataText>
              <b>Survey</b>
            </MetadataText>
            {PROJECT_CHECKIN_2 && PROJECT_CHECKIN_2.survey.length > 0 ? (
              PROJECT_CHECKIN_2.survey.map((qa, idx) => (
                <QAPair key={idx}>
                  <Question>Q: {qa.question}</Question>
                  <Answer>A: {qa.answer}</Answer>
                </QAPair>
              ))
            ) : (
              <SectionEmpty>No survey submitted.</SectionEmpty>
            )}
          </QABlock>
        </SectionContent>
      </CollapsibleSection>
      <CollapsibleSection
        title={
          <HeaderRow>
            <HeaderRowLeft>Project Upload</HeaderRowLeft>
            <HeaderStatus color={projectUpload.badgeColor}>
              {projectUpload.status}
            </HeaderStatus>
          </HeaderRow>
        }
        isOpenByDefault={latestSubmitted === PROJECT_UPLOAD}
      >
        <SectionContent>
          <SubmissionMetadata submission={PROJECT_UPLOAD} />
          <QABlock>
            <MetadataText>
              <b>Survey</b>
            </MetadataText>
            {PROJECT_UPLOAD && PROJECT_UPLOAD.survey.length > 0 ? (
              PROJECT_UPLOAD.survey.map((qa, idx) => (
                <QAPair key={idx}>
                  <Question>Q: {qa.question}</Question>
                  <Answer>A: {qa.answer}</Answer>
                </QAPair>
              ))
            ) : (
              <SectionEmpty>No survey submitted.</SectionEmpty>
            )}
          </QABlock>
          <MetadataText>
            <b>External Links</b>
          </MetadataText>
          <ExternalLinks>
            {(PROJECT_UPLOAD?.external_links ?? []).length > 0 ? (
              (PROJECT_UPLOAD?.external_links ?? []).map((link) => {
                const formattedLink = link.startsWith('http')
                  ? link
                  : `https://${link}`;
                return (
                  <li key={link}>
                    <ExternalLink
                      href={formattedLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link}
                    </ExternalLink>
                  </li>
                );
              })
            ) : (
              <SectionEmpty>No external links submitted.</SectionEmpty>
            )}
          </ExternalLinks>
          <MetadataText>
            <b>Uploaded Files</b>
          </MetadataText>
          <Files>
            {(PROJECT_UPLOAD?.file_links ?? []).length > 0 ? (
              (PROJECT_UPLOAD?.file_links ?? []).map((file) => {
                const fileName = file.split('/').pop()?.split('?')[0];
                return (
                  <li key={file}>
                    <ExternalLink
                      href={file}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {fileName}
                    </ExternalLink>
                  </li>
                );
              })
            ) : (
              <SectionEmpty>No files uploaded.</SectionEmpty>
            )}
          </Files>
        </SectionContent>
      </CollapsibleSection>

      <H4>Mentor Comments</H4>
      <P style={{ marginTop: '8px' }}>
        Use this section to leave comments about the learner's project. These
        are not visible to the learner.
      </P>
      <MultiLineInput
        style={{ marginTop: '8px' }}
        placeholder="Leave a comment for the learner"
        maxRows={10}
        value={submissionComment}
        onChange={(e) => setSubmissionComment(e.target.value)}
      />
      {submission.comment && (
        <MetadataText style={{ fontStyle: 'italic' }}>
          Comment Last Updated:{' '}
          {dayjs(submission.comment.commented_at).format('MMM D, YYYY')} by{' '}
          {submission.comment.commented_by?.name}
        </MetadataText>
      )}
      <div>
        <Button
          label={commentMutation.isLoading ? 'Saving...' : 'Save Comment'}
          faIcon="fa-solid fa-save"
          showIconBeforeLabel
          isLoading={commentMutation.isLoading}
          isDisabled={!submissionComment.trim() || commentMutation.isLoading}
          type="primary"
          onClick={() => {
            commentMutation.mutate();
          }}
        />
      </div>
    </Wrapper>
  );
}
